import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@capacit-com/lara-law-shared-ui";
import { IndexingStatusIcon } from "@/components/indexing-status/IndexingStatusIcon";
import { ContentMatchIndicator } from "@/pages/workspace/components/content-search/ContentMatchIndicator";
import {
  isActiveRevisionSearchIndexStale,
  Item,
  ITEM_TYPE_DOCUMENT,
  ITEM_TYPE_FOLDER,
  ITEM_TYPE_LINK,
} from "@/pages/workspace/utils/workspaceItemUtils";
import { getLinkHostname } from "@/pages/workspace/utils/linkUrl";
import { SubscriptionCategory } from "@/models/subscriptions/Subscription";
import useSubscriptionOperationStore from "@/stores/subscription.store";
import { Spinner } from "@capacit-com/lara-law-shared-ui";
import { Button } from "@capacit-com/lara-law-shared-ui";
import React, { useState } from "react";
import { MoreHorizontal, Home, Link2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
} from "@capacit-com/lara-law-shared-ui";
import { cn, formatBytes, formatDate, getFileTypeIconSrc } from "@/lib/utils";

import { useWorkspaceContext } from "@/pages/workspace/providers/WorkspaceContext";
import { useFileOperations } from "@/pages/workspace/hooks/useFileOperations";
import { useNavigate } from "react-router-dom";
import AppRoutes from "@/constants/routes/AppRoutes";
import { UploadSource } from "@/components/upload/types/upload-item"; // Used for source comparison in JSX
import UserAvatar from "@/components/images/UserAvatar";
import { formatUsersName } from "@/utils/format";
import FileItemDropdownMenu from "./FileItemDropdownMenu";
import SubscribeButton from "@/components/buttons/SubscribeButton";
import SubscribeAsGroupButton from "@/components/buttons/SubscribeAsGroupButton";
import ManageGroupSubscriptionModal from "@/components/modals/ManageGroupSubscriptionModal";
import FavoriteButton from "@/components/buttons/FavoriteButton";
import { DocumentCategory } from "@/models/legislation/Legislation";
import useFavoritesStore from "@/stores/favorites.store";
import useUserTokenStore from "@/stores/user-token.store";
import { Permission } from "@/lib/permissions/Permission.enum";
import { permissionMap } from "@/lib/permissions/permissionMap";
import { DocumentIndexingView } from "@/pages/workspace/utils/indexingProgress";
import {
  IndexingProgressLine,
  IndexingProgressTooltip,
} from "./IndexingProgress";

type FileListProps = {
  sortedItems: Item[];
  groupedSearchResults?: { [path: string]: Item[] };
  shouldGroupSearchResults?: boolean;
  /** Documents with an indexing run in flight, and where each is (LAR-486). */
  indexingDocuments?: ReadonlyMap<string, DocumentIndexingView>;
};

const FileList = ({
  sortedItems,
  groupedSearchResults,
  shouldGroupSearchResults,
  indexingDocuments,
}: FileListProps) => {
  const { tenantUsers, search, textOnlyMatches } = useWorkspaceContext();
  const {
    workspaceId,
    favorites,
    subscriptions,
    groupSubscriptionEntityPaths,
    draggedItem,
    dropTarget,
    itemBusy,
    busyReason,
    openDropdownId,
    setOpenDropdownId,
    setBusy,
    clearBusy,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleRowClick,
    canUserMoveItems,
  } = useFileOperations();
  const canMoveItems = canUserMoveItems();

  const hasCreateFeatureGroups = useUserTokenStore((s) =>
    s.permissions.includes(permissionMap[Permission.CreateFeatureGroups])
  );

  const [groupSubscriptionModalItem, setGroupSubscriptionModalItem] = useState<{
    entityPath: string;
    entityName: string;
    itemId: string;
    subscribeCategory: SubscriptionCategory;
  } | null>(null);

  const navigate = useNavigate();

  const isValidDropTarget = (item: Item) => {
    return (
      draggedItem !== null &&
      item.type === ITEM_TYPE_FOLDER &&
      item.id === dropTarget
    );
  };

  const breadcrumbButtonClass =
    "flex items-center cursor-pointer group relative pb-1 bg-transparent border-0 p-0 text-inherit appearance-none";
  const breadcrumbUnderlineClass =
    "absolute bottom-0 left-0 w-full h-[1.5px] bg-current/50 rounded-[0.4px] transition-all duration-200 opacity-0 group-hover:opacity-100";

  const prettifyPath = (path: string): React.ReactNode => {
    const segments = path === "root" ? [] : path.split("/").filter(Boolean);

    return (
      <div className="flex items-center gap-2 flex-wrap">
        {/* Always show Home first */}
        <button
          type="button"
          className={cn(breadcrumbButtonClass, "gap-2")}
          aria-label="Gå til filer rod"
          onClick={() =>
            navigate(AppRoutes.workspaceFiles(workspaceId), {
              state: { resetSearch: true },
            })
          }
        >
          <Home className="size-4" aria-hidden="true" />
          <span>Hjem</span>
          <span aria-hidden="true" className={breadcrumbUnderlineClass} />
        </button>

        {/* Add folder segments if any */}
        {segments.map((segment, index) => (
          <React.Fragment key={index}>
            <span className="opacity-60 select-none">&gt;</span>
            <button
              type="button"
              className={cn(breadcrumbButtonClass, "gap-1")}
              aria-label={`Gå til mappe ${segment}`}
              onClick={() =>
                navigate(
                  AppRoutes.workspaceFilesFolder(
                    workspaceId,
                    segments
                      .slice(0, index + 1)
                      .map((s) => encodeURIComponent(s))
                      .join("/")
                  ),
                  { state: { resetSearch: true } }
                )
              }
            >
              <img
                src={getFileTypeIconSrc("folder") || ""}
                alt=""
                className="size-5"
              />
              <span>{segment}</span>
              <span aria-hidden="true" className={breadcrumbUnderlineClass} />
            </button>
          </React.Fragment>
        ))}
      </div>
    );
  };

  // Helper function to render a single item row
  const renderItemRow = (item: Item) => {
    const isFolder = item.type === ITEM_TYPE_FOLDER;
    const isDocument = item.type === ITEM_TYPE_DOCUMENT;
    const isLink = item.type === ITEM_TYPE_LINK;
    const textMatch = textOnlyMatches.get(item.id);
    const isDragging = draggedItem === item.id;
    const isDropTargetItem = isValidDropTarget(item);
    const isFavorite = favorites?.workspace?.includes(item.id) ?? false;

    const entityPath =
      item.type === ITEM_TYPE_FOLDER
        ? `workspace/${workspaceId}/folder/${item.id}`
        : `workspace/${workspaceId}/document/${item.id}`;
    const isSubscribed =
      Array.isArray(subscriptions) &&
      subscriptions.some((s) => s.entityPath === entityPath);
    const isGroupSubscribed =
      groupSubscriptionEntityPaths?.has(entityPath) ?? false;
    const documentOwner = tenantUsers.find((user) => user.id === item.userId);
    const indexing = indexingDocuments?.get(item.id);

    return (
      <TableRow
        key={item.id}
        className={cn(
          "group relative cursor-pointer transition-all duration-200 hover:bg-muted/30",
          {
            "opacity-50": isDragging,
            "bg-muted": dropTarget === item.id,
            "opacity-50 pointer-events-none": itemBusy[item.id],
          }
        )}
        // Only allow dragging if the user may move items and is not searching.
        // Later on when we have time we can add logic so you can drag across folders.
        draggable={canMoveItems && search.searchString === ""}
        onDragStart={(e) => !itemBusy[item.id] && handleDragStart(e, item.id)}
        onDragOver={(e) => !itemBusy[item.id] && handleDragOver(e, item.id)}
        onDragLeave={() => !itemBusy[item.id] && handleDragLeave}
        onDrop={(e) =>
          draggedItem &&
          !itemBusy[item.id] &&
          handleDrop(e, item.id, draggedItem)
        }
        onClick={(e) => !itemBusy[item.id] && handleRowClick(item, e)}
        style={{
          backgroundImage:
            !itemBusy[item.id] && isDropTargetItem
              ? "repeating-linear-gradient(45deg, transparent, transparent 5px, rgba(59, 130, 246, 0.1) 5px, rgba(59, 130, 246, 0.1) 10px)"
              : "none",
          boxShadow:
            !itemBusy[item.id] && isDropTargetItem
              ? "0 0 0 2px rgba(59, 130, 246, 0.5)"
              : "none",
        }}
      >
        <TableCell className="font-medium relative group max-w-100 min-w-30">
          <div className="flex items-center w-full justify-between relative">
            {/* File name & icon with grow */}
            <div
              className={`flex items-center gap-2 truncate ${
                openDropdownId === item.id
                  ? "max-w-[calc(100%-120px)]"
                  : itemBusy[item.id]
                    ? "max-w-[calc(100%-170px)]"
                    : (isFavorite && !isSubscribed) ||
                        (isSubscribed && !isFavorite)
                      ? "max-w-[calc(100%-30px)]"
                      : isFavorite && isSubscribed
                        ? "max-w-[calc(100%-60px)]"
                        : "max-w-full"
              } group-hover:max-w-[calc(100%-120px)]`}
            >
              {isLink ? (
                <Link2
                  className="size-5 shrink-0 text-muted-foreground"
                  aria-hidden="true"
                />
              ) : (
                <img
                  src={
                    getFileTypeIconSrc(
                      item.type === ITEM_TYPE_FOLDER
                        ? "folder"
                        : item.documentType
                    ) || ""
                  }
                  alt={item.documentType || ""}
                  className={cn("size-5")}
                />
              )}
              {isLink && item.url ? (
                // A real anchor, so middle-click, "copy link address" and the
                // status-bar preview all work; the row click opens it too.
                <a
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="truncate hover:underline"
                  title={item.url}
                  onClick={(e) => e.stopPropagation()}
                >
                  {item.name}
                </a>
              ) : indexing ? (
                // A second line while kildeklargøring runs: the Status column
                // is hidden below lg, and a spinner alone told users nothing.
                <div className="flex min-w-0 flex-col">
                  <span className="truncate">{item.name}</span>
                  <IndexingProgressLine
                    view={indexing}
                    className="truncate font-normal"
                  />
                </div>
              ) : (
                <span className="truncate">{item.name}</span>
              )}
              {textMatch && (
                <ContentMatchIndicator
                  count={textMatch.contentMatchCount}
                  indexStale={textMatch.searchIndexStale}
                />
              )}
            </div>
            {itemBusy[item.id] ? (
              <div className="absolute right-0 top-1/2 -translate-y-1/2">
                <div className="flex gap-1">
                  <Spinner className="size-4" />
                  <span className="text-xs text-muted-foreground">
                    {busyReason[item.id]}
                  </span>
                </div>
              </div>
            ) : (
              <div className="absolute right-0 top-1/2 -translate-y-1/2 flex items-center gap-1">
                <DropdownMenu
                  open={openDropdownId === item.id}
                  onOpenChange={(open) => {
                    if (open) {
                      setOpenDropdownId(item.id);
                    } else {
                      setOpenDropdownId(null);
                    }
                  }}
                  modal={false}
                >
                  <DropdownMenuTrigger
                    asChild
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Button
                      variant="ghost"
                      size="icon"
                      className={cn(
                        "size-6 transition-opacity duration-200 hover:bg-muted/80",
                        openDropdownId === item.id
                          ? "opacity-100 block"
                          : "hidden opacity-0 group-hover:block group-hover:opacity-100"
                      )}
                    >
                      <div className="flex items-center justify-center">
                        <MoreHorizontal className="h-4 w-4" />
                      </div>
                    </Button>
                  </DropdownMenuTrigger>
                  <FileItemDropdownMenu item={item} />
                </DropdownMenu>
                {/* Favorites and subscriptions address documents and folders; a link is neither. */}
                {!isLink && (
                  <>
                    <div
                      className={cn(
                        "flex flex-row group-hover:flex",
                        !(
                          openDropdownId === item.id ||
                          isSubscribed ||
                          useSubscriptionOperationStore
                            .getState()
                            .isPending(entityPath)
                        ) && "hidden"
                      )}
                    >
                      <SubscribeButton
                        subscribeCategory={
                          item.type === ITEM_TYPE_FOLDER
                            ? SubscriptionCategory.Workspace
                            : SubscriptionCategory.WorkspaceDocument
                        }
                        entityPath={entityPath}
                        entityName={item.name}
                        source="workspace"
                        isSubscribed={isSubscribed}
                        className="size-6 hover:bg-muted/80"
                        onOperationStart={() => {
                          setBusy(item.id, "subscription");
                        }}
                        onOperationEnd={() => {
                          clearBusy(item.id);
                        }}
                      />
                    </div>
                    <div
                      className={cn(
                        "flex flex-row group-hover:flex",
                        !(openDropdownId === item.id || isGroupSubscribed) &&
                          hasCreateFeatureGroups &&
                          "hidden",
                        !hasCreateFeatureGroups &&
                          !isGroupSubscribed &&
                          "hidden"
                      )}
                    >
                      <SubscribeAsGroupButton
                        subscribeCategory={
                          item.type === ITEM_TYPE_FOLDER
                            ? SubscriptionCategory.Workspace
                            : SubscriptionCategory.WorkspaceDocument
                        }
                        entityPath={entityPath}
                        entityName={item.name}
                        source="workspace"
                        isGroupSubscribed={isGroupSubscribed}
                        className="size-6 hover:bg-muted/80"
                        open={
                          groupSubscriptionModalItem?.entityPath === entityPath
                        }
                        onOpenChange={(open) => {
                          if (!open) setGroupSubscriptionModalItem(null);
                        }}
                        onOpenRequest={() => {
                          setGroupSubscriptionModalItem({
                            entityPath,
                            entityName: item.name,
                            itemId: item.id,
                            subscribeCategory:
                              item.type === ITEM_TYPE_FOLDER
                                ? SubscriptionCategory.Workspace
                                : SubscriptionCategory.WorkspaceDocument,
                          });
                        }}
                        onOperationStart={() => {
                          setBusy(item.id, "subscription");
                        }}
                        onOperationEnd={() => {
                          clearBusy(item.id);
                        }}
                      />
                    </div>

                    <div
                      className={cn(
                        "flex flex-row group-hover:flex",
                        !(
                          openDropdownId === item.id ||
                          isFavorite ||
                          useFavoritesStore.getState().isPending(item.id)
                        ) && "hidden"
                      )}
                    >
                      <FavoriteButton
                        isHovering={true}
                        documentCategory={DocumentCategory.Workspace}
                        documentId={item.id}
                        title={item.name}
                        isFavorite={isFavorite}
                        workspaceId={workspaceId}
                        className="size-6 hover:bg-muted/80"
                        onOperationStart={() => {
                          setBusy(item.id, "favorite");
                        }}
                        onOperationEnd={() => {
                          clearBusy(item.id);
                        }}
                      />
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </TableCell>
        <TableCell className="hidden md:table-cell max-w-24 truncate">
          {formatDate(item.updatedAt || item.createdAt || "")}
        </TableCell>
        <TableCell className="hidden lg:table-cell max-w-18 truncate">
          {isDocument
            ? formatBytes(item.size || 0)
            : isFolder
              ? `${item.fileCount || 0} filer`
              : "-"}
        </TableCell>
        <TableCell className="hidden lg:table-cell max-w-18 truncate">
          {isLink
            ? getLinkHostname(item.url ?? "")
            : item.documentSource === UploadSource.LOCAL
              ? "Lokal"
              : (item.documentSource ?? "")}
        </TableCell>
        <TableCell className="hidden lg:table-cell max-w-18">
          {isDocument && (
            <IndexingStatusIcon
              className="ml-2 size-6"
              isIndexed={
                item.revisions?.find(
                  (r) => r.revisionVersion === item.activeRevision
                )?.isIndexed === true
              }
              searchIndexStale={isActiveRevisionSearchIndexStale(item)}
              documentType={item.documentType}
              isIndexing={indexing !== undefined}
              indexingTooltip={
                indexing && <IndexingProgressTooltip view={indexing} />
              }
            />
          )}
        </TableCell>
        <TableCell className="hidden md:table-cell max-w-32 truncate">
          <div className="flex items-center gap-2 min-w-0">
            <UserAvatar
              user={documentOwner}
              avatarClassName="size-5 shrink-0"
            />
            <span className="truncate min-w-0">
              {formatUsersName(documentOwner)}
            </span>
          </div>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <>
      <Table className="w-full">
        <TableHeader>
          <TableRow>
            <TableHead className="max-w-100 min-w-30">Navn</TableHead>
            <TableHead className="hidden md:table-cell max-w-26">
              Ændret
            </TableHead>
            <TableHead className="hidden lg:table-cell max-w-18">
              Størrelse
            </TableHead>
            <TableHead className="hidden lg:table-cell max-w-18">
              Kilde
            </TableHead>
            <TableHead className="hidden lg:table-cell max-w-18">
              Status
            </TableHead>
            <TableHead className="hidden md:table-cell max-w-32">
              Oprettet af
            </TableHead>
          </TableRow>
        </TableHeader>
        {/*
        Firefox preview occasionally drops collapsed table row borders,
        so we render separators with inset cell shadows instead.
      */}
        <TableBody className="[&>tr]:border-b-0 [&>tr>td]:shadow-[inset_0_-1px_0_var(--border)] [&>tr:last-child>td]:shadow-none">
          {shouldGroupSearchResults && groupedSearchResults
            ? // Render grouped search results with folder path headers
              Object.entries(groupedSearchResults).map(([path, items]) => (
                <React.Fragment key={path}>
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="font-semibold text-sm py-2 bg-accent/90 dark:bg-accent text-accent-foreground overflow-visible"
                    >
                      <div className="flex items-center gap-2">
                        {prettifyPath(path)}
                      </div>
                    </TableCell>
                  </TableRow>
                  {/* Items in this path */}
                  {items.map((item) => renderItemRow(item))}
                </React.Fragment>
              ))
            : // Render regular sorted items
              sortedItems.map((item) => renderItemRow(item))}
        </TableBody>
      </Table>

      {groupSubscriptionModalItem && (
        <ManageGroupSubscriptionModal
          open={true}
          onOpenChange={(open) => {
            if (!open) {
              clearBusy(groupSubscriptionModalItem.itemId);
              setGroupSubscriptionModalItem(null);
            }
          }}
          variant="default"
          entityPath={groupSubscriptionModalItem.entityPath}
          entityName={groupSubscriptionModalItem.entityName}
          source="workspace"
          subscribeCategory={groupSubscriptionModalItem.subscribeCategory}
        />
      )}
    </>
  );
};

export default FileList;
